import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';

export interface FlowStep {
  /** Visible label. One to three words — a stepper label is a signpost. */
  label: string;
  /** Route to return to when this step is already behind the customer. */
  route?: string;
}

interface NavigationStatusProps {
  /** 1-based. A value past the last numbered step marks the flow complete. */
  step: number;
  /** State to preserve when navigating back. */
  locationState?: any;
  /**
   * Overrides the route-derived steps. `OxfordPackages` runs its flow in
   * local state on a single route, so it supplies its own list and handles
   * the going-back itself.
   */
  steps?: FlowStep[];
  onStepSelect?: (step: number) => void;
  /** Label for the decorative end-of-journey node. */
  destinationLabel?: string;
}

// Short labels by design. A stepper says where you are, not what you must do
// — the page's own <h1> carries that. The removals flow previously rendered
// full sentences ("More info about your type of move") in a second row of
// markup below the indicator; those are folded in here.
const REMOVALS_STEPS: FlowStep[] = [
  { label: 'Move type', route: '/removals' },
  { label: 'Move details', route: '/removals/questions' },
  { label: 'Storage options', route: '/removals/extras' },
  { label: 'Addresses', route: '/removals/collection_address' },
  { label: 'Payment', route: '/removals/payment' },
];

function bookingSteps(baseRoute: string): FlowStep[] {
  return [
    { label: 'Your items', route: `${baseRoute}/boxes` },
    { label: 'Collection and delivery', route: `${baseRoute}/collection_address` },
    { label: 'Payment', route: `${baseRoute}/payment` },
  ];
}

function TickIcon(): JSX.Element {
  return (
    <svg
      className="step-indicator__tick"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m5 13 5 5L20 7" />
    </svg>
  );
}

/**
 * The destination marker. Inline SVG rather than the `/img/box-icon-white.png`
 * this used to render: the strokes are `currentColor`, so the glyph inherits
 * the marker's state — slate line art while the journey is ahead of you,
 * white once it is behind you — instead of a white raster faded with
 * `opacity: 0.45` to fake an unreached state. Same reasoning as
 * `TileIcons.tsx` (DESIGN_LANGUAGE.md Phase 4.4 item 5): one asset serves
 * Kit Keeper, Keepr and St Anne's, because an external image cannot inherit
 * page colour.
 */
function BoxIcon(): JSX.Element {
  return (
    <svg
      className="step-indicator__box"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3.5 7.6 12 3.2l8.5 4.4v8.8L12 20.8l-8.5-4.4V7.6Z" />
      <path d="m3.5 7.6 8.5 4.4 8.5-4.4" />
      <path d="M12 12v8.8" />
      <path d="m7.75 5.4 8.5 4.4" />
    </svg>
  );
}

function NavigationStatus({
  step,
  locationState,
  steps: stepsOverride,
  onStepSelect,
  destinationLabel,
}: NavigationStatusProps): JSX.Element {
  const location = useLocation();
  const history = useHistory();

  const isRemovalsFlow = location.pathname.includes('/removals');
  const isSuitcaseFlow = location.pathname.includes('/suitcase');
  const isStorageFlow = location.pathname.includes('/storage');
  const isShippingFlow = location.pathname.includes('/shipping');

  let steps: FlowStep[];
  if (stepsOverride) {
    steps = stepsOverride;
  } else if (isRemovalsFlow) {
    steps = REMOVALS_STEPS;
  } else if (isStorageFlow) {
    steps = bookingSteps('/storage');
  } else if (isShippingFlow) {
    steps = bookingSteps('/shipping');
  } else if (isSuitcaseFlow) {
    steps = bookingSteps('/suitcase');
  } else {
    steps = bookingSteps('');
  }

  // The end-of-journey node. It was a 100px purple disc rendered as a fourth
  // `.indicator-line` after step 3 — visually a step, semantically nothing,
  // with an empty-alt image inside. It is now a named destination, which is
  // also what makes `<NavigationStatus step={4} />` on the Success page mean
  // something: every step behind you, and you have arrived.
  const destination =
    destinationLabel ??
    (isRemovalsFlow ? 'Moved' : isStorageFlow ? 'Stored' : 'Shipped');

  const isComplete = step > steps.length;
  const currentIndex = Math.min(Math.max(step, 1), steps.length) - 1;
  const currentLabel = isComplete ? destination : steps[currentIndex].label;

  const goToStep = (stepNumber: number, route?: string) => {
    if (onStepSelect) {
      onStepSelect(stepNumber);
      return;
    }
    if (route) {
      history.push(route, locationState || location.state);
    }
  };

  return (
    <nav className="step-indicator" aria-label="Booking progress">
      <ol className="step-indicator__list">
        {steps.map((flowStep, index) => {
          const number = index + 1;
          const state =
            number < step ? 'complete' : number === step ? 'current' : 'upcoming';
          // Only steps already behind the customer are navigable — going
          // forward has to happen through the form, which validates.
          const navigable =
            state === 'complete' && Boolean(flowStep.route || onStepSelect);

          return (
            <li
              key={flowStep.label}
              className={`step-indicator__step step-indicator__step--${state}`}
            >
              {navigable ? (
                <button
                  type="button"
                  className="step-indicator__marker"
                  onClick={() => goToStep(number, flowStep.route)}
                  aria-label={`Back to step ${number}, ${flowStep.label}`}
                >
                  <TickIcon />
                </button>
              ) : (
                <span
                  className="step-indicator__marker"
                  aria-current={state === 'current' ? 'step' : undefined}
                >
                  {state === 'complete' ? <TickIcon /> : number}
                </span>
              )}
              <span className="step-indicator__label">{flowStep.label}</span>
            </li>
          );
        })}

        <li
          className={`step-indicator__step step-indicator__step--destination step-indicator__step--${
            isComplete ? 'complete' : 'upcoming'
          }`}
        >
          <span className="step-indicator__marker">
            <BoxIcon />
          </span>
          <span className="step-indicator__label">{destination}</span>
        </li>
      </ol>

      {/*
        On a phone the per-step labels are hidden visually (they stay in the
        DOM for assistive tech, which reads the list) and this single line
        carries the position instead. Five removals labels across 360px is
        unreadable; "Step 2 of 5" is not.
      */}
      <p className="step-indicator__position" aria-hidden="true">
        {isComplete
          ? currentLabel
          : `Step ${step} of ${steps.length} — ${currentLabel}`}
      </p>
    </nav>
  );
}

export default NavigationStatus;
